import { defineStore, acceptHMRUpdate } from 'pinia';
import { computed, ref } from 'vue'
import type { Ref } from 'vue'
import type { LoginResponseData, TokenResponseData, TelegramUserType, EmailVerifyCodeBody } from 'src/types/auth'
import { authApi, profileApi } from 'src/api'
import { castProfile, type ProfileType } from 'src/types/profile'
import type { WebAppInitData } from '@twa-dev/types'


type ApplyProfileOptions = {
  patch: boolean
}

const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'


export const useAuthStore = defineStore('authStore', () => {
  const profile: Ref<ProfileType | null> = ref(null)
  const accessToken: Ref<string | null> = ref(localStorage.getItem(ACCESS_TOKEN_KEY) || null)
  const refreshToken: Ref<string | null> = ref(localStorage.getItem(REFRESH_TOKEN_KEY) || null)
  const authDirty = ref(true)
  const isSignedIn = computed(() => accessToken.value !== null)

  function applyLogin (response: TokenResponseData | LoginResponseData) {
    accessToken.value = response.access_token
    refreshToken.value = response.refresh_token
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken.value)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken.value)
  }

  function applyProfile (response: ProfileType, options?: ApplyProfileOptions) {
    if (options?.patch && profile.value) {
      for (const property in response) {
        // @ts-expect-error patch keys
        profile.value[property] = response[property]
      }
    } else {
      profile.value = response
    }
  }

  async function updateProfile () {
    try {
      const profileData = await profileApi.getProfile()
      applyProfile(castProfile(profileData))
    } catch (e) {
      // TODO: logout, ведь все равнр нельзя извлечь данные
      console.error(e)
    }
  }

  // новая сессия
  if (accessToken.value) {
    updateProfile()
      .catch(e => console.error(e))
      .finally(() => authDirty.value = false)

  } else {
    authDirty.value = false
  }

  async function emailLogin (body: EmailVerifyCodeBody) {
    const response = await authApi.emailVerifyCode(body)
    applyLogin(response)
    await updateProfile()
  }

  async function telegramLogin (body: TelegramUserType) {
    const response = await authApi.telegramAuth(body)
    applyLogin(response)
    await updateProfile()
  }

  async function telegramTMALogin (body: WebAppInitData & { init_data: string }) {
    const response = await authApi.telegramTMAAuth(body)
    applyLogin(response)
    await updateProfile()
  }

  function logout () {
    authDirty.value = false
    profile.value = null
    accessToken.value = null
    refreshToken.value = null

    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  }

  return {
    accessToken,
    applyLogin,
    authDirty,
    emailLogin,
    isSignedIn,
    logout,
    profile,
    refreshToken,
    telegramLogin,
    telegramTMALogin,
    updateProfile,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}

export default useAuthStore
